import { TransformWrapper, TransformComponent, useControls } from "react-zoom-pan-pinch";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ZoomableImageProps {
  src: string;
  alt: string;
}

const Controls = () => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <div className="absolute top-4 right-4 z-10 flex gap-2 bg-background/80 backdrop-blur-sm rounded-lg p-2 shadow-lg">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => zoomIn()}
        className="h-8 w-8"
        aria-label="Zoom in"
      >
        <ZoomIn className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => zoomOut()}
        className="h-8 w-8"
        aria-label="Zoom out"
      >
        <ZoomOut className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => resetTransform()}
        className="h-8 w-8"
        aria-label="Reset zoom"
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
    </div>
  );
};

const ZoomableImage = ({ src, alt }: ZoomableImageProps) => {
  return (
    <div className="relative w-full h-full flex flex-col">
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={5}
        centerOnInit
        limitToBounds={false}
        centerZoomedOut={false}
        velocityAnimation={{ disabled: true }}
        panning={{ disabled: false, velocityDisabled: true }}
        wheel={{ smoothStep: 0.1 }}
        pinch={{ step: 10 }}
        doubleClick={{ mode: "reset" }}
      >
        <Controls />

        {/* Image Container */}
        <TransformComponent
          wrapperStyle={{
            width: "100%",
            height: "100%",
          }}
          contentStyle={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-full object-contain select-none cursor-grab active:cursor-grabbing"
            draggable={false}
          />
        </TransformComponent>

        {/* Instructions */}
        <div className="absolute bottom-4 right-4 z-10 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-1 text-xs text-muted-foreground hidden md:block">
          Scroll to zoom • Double-click to reset • Drag to pan
        </div>
        <div className="absolute bottom-4 right-4 z-10 bg-background/80 backdrop-blur-sm rounded-lg px-3 py-1 text-xs text-muted-foreground md:hidden">
          Pinch to zoom • Double-tap to reset • Drag to pan
        </div>
      </TransformWrapper>
    </div>
  );
};

export default ZoomableImage;
